import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { PageMeta } from '@/components/seo/page-meta'
import { PELGO_ZOOM_DOCS_SEO } from '@/lib/pelgo-seo'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

const linkClass = 'text-blue-600 hover:underline'
const h2Class = 'text-2xl font-semibold text-slate-900 mb-3'
const h3Class = 'text-lg font-semibold text-slate-900 mt-6 mb-2'
const pClass = 'text-slate-700 leading-relaxed'
const listClass = 'pl-6 text-slate-700 space-y-2'

const FAQ = [
  {
    q: 'Do my clients need to install the app or connect Zoom?',
    a: 'No. Only the coach connects Zoom. Clients receive a normal Zoom join link in their booking confirmation.',
  },
  {
    q: 'Which Zoom account should I connect?',
    a: 'The Zoom account you use to host coaching sessions. The meetings are created under that account, using its settings and licence.',
  },
  {
    q: 'What does Pelgo see in my Zoom account?',
    a: 'Only your Zoom user ID, name and email address, and the meetings Pelgo creates on your behalf. Pelgo does not access your recordings, chat, contacts, or other meetings.',
  },
  {
    q: 'Does Pelgo read my other meetings or recordings?',
    a: 'No. The app requests permission only to create, view, update, and delete meetings, and to read your basic Zoom user profile.',
  },
  {
    q: 'I removed the app in Zoom. What happens to my Pelgo sessions?',
    a: 'Pelgo removes the stored Zoom connection and switches your preferred meeting tool back to Google Meet. Existing Zoom meetings stay in your Zoom account, but Pelgo can no longer update or cancel them.',
  },
  {
    q: 'Does the app send marketing emails?',
    a: 'No. The app does not send email. Booking notifications come from Pelgo and are governed by your Pelgo notification settings.',
  },
]

export function ZoomDocsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={PELGO_ZOOM_DOCS_SEO} />
      <MarketingNav variant="standalone" />

      <main className="container max-w-4xl py-12 px-4 pt-32 flex-1">
        <h1 className="text-4xl font-medium text-slate-900 mb-4">Pelgo for Zoom: Documentation</h1>
        <p className={`${pClass} mb-6`}>
          Pelgo for Zoom lets coaches on the Pelgo platform host their coaching sessions on Zoom.
          After you connect your Zoom account, Pelgo automatically creates a Zoom meeting for each
          booked session, keeps it up to date if the session is rescheduled, and deletes it if the
          session is canceled.
        </p>
        <nav aria-label="On this page" className="mb-10">
          <ul className="list-disc pl-6 text-slate-700 space-y-1">
            <li>
              <a href="#add" className={linkClass}>
                Adding the app
              </a>
            </li>
            <li>
              <a href="#usage" className={linkClass}>
                Usage
              </a>
            </li>
            <li>
              <a href="#remove" className={linkClass}>
                Removing the app
              </a>
            </li>
            <li>
              <a href="#troubleshooting" className={linkClass}>
                Troubleshooting
              </a>
            </li>
            <li>
              <a href="#faq" className={linkClass}>
                FAQ
              </a>
            </li>
            <li>
              <a href="#support" className={linkClass}>
                Contact support
              </a>
            </li>
          </ul>
        </nav>

        <div className="space-y-12">
          <section id="add">
            <h2 className={h2Class}>Adding the app</h2>
            <h3 className={h3Class}>Before you start</h3>
            <ul className={`${listClass} list-disc`}>
              <li>You need an approved coach account on Pelgo (coach.pelgo.com).</li>
              <li>You need a Zoom account that can host meetings.</li>
            </ul>
            <h3 className={h3Class}>Steps</h3>
            <ol className={`${listClass} list-decimal`}>
              <li>Sign in to the Pelgo coach portal at coach.pelgo.com.</li>
              <li>
                Open <strong>Availability</strong>. In the <strong>Zoom</strong> card, click{' '}
                <strong>Connect Zoom</strong>.
              </li>
              <li>Sign in to Zoom if asked, then review the permissions requested.</li>
              <li>
                Click <strong>Allow</strong>. You return to Pelgo and the card shows{' '}
                <strong>Connected as your-zoom-email</strong>.
              </li>
              <li>
                In the meeting tool setting on the same page, select <strong>Zoom</strong>. New
                bookings will now get a Zoom meeting.
              </li>
            </ol>
            <p className={`${pClass} mt-4`}>
              <strong>Starting from the Zoom App Marketplace:</strong> if you click{' '}
              <strong>Add</strong> on the Pelgo listing in Zoom, you are sent to the Pelgo coach
              portal. Sign in and click <strong>Connect Zoom</strong> in the Zoom card to finish.
              The app is not active until you complete this step.
            </p>
            <h3 className={h3Class}>Permissions requested</h3>
            <ul className={`${listClass} list-disc`}>
              <li>
                <code>user:read:user</code>: read your Zoom user ID, name, and email to show which
                account is connected.
              </li>
              <li>
                <code>meeting:write:meeting</code>: create the Zoom meeting for a booked session.
              </li>
              <li>
                <code>meeting:update:meeting</code>: update the meeting when a session is
                rescheduled.
              </li>
              <li>
                <code>meeting:read:meeting</code>: read the meeting to get its join and start links.
              </li>
              <li>
                <code>meeting:delete:meeting</code>: delete the meeting when a session is canceled.
              </li>
            </ul>
            <p className={`${pClass} mt-4`}>
              Having trouble? See{' '}
              <a href="#troubleshooting" className={linkClass}>
                Troubleshooting
              </a>
              .
            </p>
          </section>

          <section id="usage">
            <h2 className={h2Class}>Usage</h2>
            <p className={pClass}>
              Once Zoom is connected and selected as your meeting tool, there is nothing else to set
              up.
            </p>
            <h3 className={h3Class}>A client books a session</h3>
            <p className={pClass}>
              Pelgo creates a Zoom meeting in your account for the session time. The meeting has the
              waiting room on and does not let participants join before you. The client receives the
              join link in their confirmation.
            </p>
            <h3 className={h3Class}>Starting a session</h3>
            <p className={pClass}>
              In the coach portal, open <strong>Sessions</strong> or your dashboard and click the
              Zoom start button on the session. Zoom opens with you as the host.
            </p>
            <h3 className={h3Class}>Rescheduling or canceling</h3>
            <p className={pClass}>
              When a session is rescheduled, the same Zoom meeting is updated to the new time, so
              the join link does not change. When a session is canceled, the Zoom meeting is
              deleted.
            </p>
            <h3 className={h3Class}>Choosing between Zoom and Google Meet</h3>
            <p className={pClass}>
              You can switch your preferred meeting tool at any time on the Availability page. The
              change applies to new bookings.
            </p>
          </section>

          <section id="remove">
            <h2 className={h2Class}>Removing the app</h2>
            <h3 className={h3Class}>From Pelgo</h3>
            <ol className={`${listClass} list-decimal`}>
              <li>Sign in to the Pelgo coach portal and open Availability.</li>
              <li>
                In the Zoom card, click <strong>Disconnect</strong> and confirm.
              </li>
            </ol>
            <h3 className={h3Class}>From Zoom</h3>
            <ol className={`${listClass} list-decimal`}>
              <li>Sign in to your Zoom account and open the Zoom App Marketplace.</li>
              <li>
                Click <strong>Manage</strong>, then <strong>Added Apps</strong>, or search for
                &quot;Pelgo&quot;.
              </li>
              <li>Select the Pelgo app.</li>
              <li>
                Click <strong>Remove</strong>.
              </li>
            </ol>
            <h3 className={h3Class}>What happens when you remove the app</h3>
            <ul className={`${listClass} list-disc`}>
              <li>
                Pelgo can no longer create, update, or cancel Zoom meetings for you. Your preferred
                meeting tool switches to Google Meet for new bookings.
              </li>
              <li>
                Meetings already created in Zoom stay in your Zoom account. Delete or reschedule
                them in Zoom if a session changes.
              </li>
              <li>
                From Pelgo, Disconnect is not available while you have upcoming Zoom sessions.
                Complete or cancel them first, or remove the app from Zoom.
              </li>
            </ul>
            <h3 className={h3Class}>What happens to your data</h3>
            <p className={pClass}>
              When you disconnect or remove the app, Pelgo revokes its access and deletes the stored
              Zoom authorization tokens and your Zoom account link. Zoom meeting IDs and join links
              remain on past coaching session records, which are part of your Pelgo account history.
              To have your Pelgo data deleted, contact us using the{' '}
              <a href="#support" className={linkClass}>
                support options below
              </a>
              . See our{' '}
              <Link to="/privacy" className={linkClass}>
                Privacy Notice
              </Link>{' '}
              for details.
            </p>
          </section>

          <section id="troubleshooting">
            <h2 className={h2Class}>Troubleshooting</h2>
            <h3 className={h3Class}>I clicked Add in Zoom but nothing is connected</h3>
            <p className={pClass}>
              Adding the app from Zoom only starts the process. Sign in to the Pelgo coach portal
              and click <strong>Connect Zoom</strong> on the Availability page.
            </p>
            <h3 className={h3Class}>
              &quot;The connection request expired. Please start again.&quot;
            </h3>
            <p className={pClass}>
              The authorization link is valid for 10 minutes and must be completed in the same
              browser where you clicked Connect Zoom. Start again from the Zoom card.
            </p>
            <h3 className={h3Class}>&quot;Zoom authorization was canceled&quot;</h3>
            <p className={pClass}>
              You clicked Cancel or closed the Zoom consent screen. Click Connect Zoom again and
              choose Allow.
            </p>
            <h3 className={h3Class}>My Zoom account is not accepted</h3>
            <p className={pClass}>
              Some Zoom accounts are managed by an administrator who must approve apps. Ask your
              Zoom admin to allow the Pelgo app, or use a Zoom account you manage.
            </p>
            <h3 className={h3Class}>&quot;Reconnect the same Zoom account&quot;</h3>
            <p className={pClass}>
              You have upcoming sessions whose meetings belong to a different Zoom account. Connect
              the original account, or cancel those sessions first.
            </p>
            <h3 className={h3Class}>Zoom must be reconnected</h3>
            <p className={pClass}>
              If Zoom access expired or was revoked, bookings cannot create meetings and the Zoom
              card asks you to reconnect. Click Connect Zoom again.
            </p>
            <h3 className={h3Class}>The Zoom start button does not open</h3>
            <p className={pClass}>
              Allow pop-ups for coach.pelgo.com, then try again. If it still fails, reconnect Zoom.
            </p>
          </section>

          <section id="faq">
            <h2 className={h2Class}>FAQ</h2>
            <dl className="space-y-5">
              {FAQ.map((item) => (
                <div key={item.q}>
                  <dt className="font-semibold text-slate-900">{item.q}</dt>
                  <dd className={`${pClass} mt-1`}>{item.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section id="support">
            <h2 className={h2Class}>Contact support</h2>
            <ul className={`${listClass} list-disc`}>
              <li>Hours: Monday to Thursday, 9:00 AM to 12:00 PM ET and 9:00 PM to 12:00 AM ET.</li>
              <li>First response: within 24 hours.</li>
              <li>
                Create a support case:{' '}
                <Link to="/contact" className={linkClass}>
                  contact form
                </Link>
              </li>
              <li>
                Email:{' '}
                <a href="mailto:support@pelgo.com" className={linkClass}>
                  support@pelgo.com
                </a>
              </li>
              <li>
                More details on our{' '}
                <Link to="/zoom/support" className={linkClass}>
                  support page
                </Link>
                .
              </li>
            </ul>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
